import React, { useState } from "react";
import { IonButton, IonRow, IonCol, IonItem, IonInput, IonCard, IonSearchbar, IonCardTitle } from "@ionic/react";
import { getRidersByNameGSI } from "../../utilities/rider/Rider";
import { Barn, Rider } from "../../models";
import { getBarnById } from "../../utilities/barn/Barn";
import BasicModal from "../Modals/BasicModal";

interface VerifyRiderModalProps {
    currentValue?: formattedOption | null | undefined;
    handleCancel: () => void;
    handleUseMatchedRider: (e: formattedOption) => void;
    handleCreateNewRider: (newRiderName: string) => void;
    selectedBarn?: Barn | null;
}

export interface formattedOption {
    value: string; //Rider's id
    label: string; //Used for filtering options as a user types
    riderName: string; //Rider's Show Name - will display when item is selected
    riderNameLabel?: string; //Rider's Show Name + Nickname - will display in options
    createdByLabel?: string;
    barnLabel?: string; //Barn Name - will display in options
    riderStatusLabel?: string; //Rider's status
    rider?: Rider;
}

const VerifyRiderModal: React.FC<VerifyRiderModalProps> = ({ currentValue, handleCancel, handleUseMatchedRider, handleCreateNewRider, selectedBarn }) => {
    // Track the user's confirmed rider name, which will be used to search the DB for the rider
    const [unConfirmedRiderName, setUnConfirmedRiderName] = useState(currentValue?.riderName || "");
    const [confirmedRiderName, setConfirmedRiderName] = useState("");
    const [isNameConfirmed, setIsNameConfirmed] = useState(false);

    // Store in state all riders that match the confirmed rider name in the DB
    const [formattedMatchedRiders, setFormattedMatchedRiders] = useState<formattedOption[] | null | undefined>();
    const [filteredFormattedMatchedRiders, setFilteredFormattedMatchedRiders] = useState<formattedOption[] | null | undefined>();
    const [searchText, setSearchText] = useState("");

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    /**
     * User confirmed the rider name
     * Search the DB by this rider name (Server-Side Filtering)
     * Only get the rider fields needed (Projection)
     */
    const handleClickConfirm = async () => {
        setError("");
        if (unConfirmedRiderName === "") {
            setError("Please include a rider name.");
        } else {
            const formattedUnconfirmedName = unConfirmedRiderName.trim();
            setUnConfirmedRiderName(formattedUnconfirmedName);
            setConfirmedRiderName(formattedUnconfirmedName);
            setIsNameConfirmed(true);
            setIsLoading(true);
            const queryResult = await getRidersByNameGSI(formattedUnconfirmedName, true);
            console.log("queryResult", queryResult);
            if (queryResult.isSuccess) {
                const ridersFromDB = queryResult.result;
                const formattedRiders = await formatRiders(ridersFromDB);
                setFormattedMatchedRiders(formattedRiders);
                setFilteredFormattedMatchedRiders(formattedRiders);
            }
            setIsLoading(false);
        }
    };

    const formatRiders = async (riders: Rider[]) => {
        let formatted: formattedOption[] = [];
        for (let i = 0; i < riders.length; i++) {
            const rider = riders[i];
            const formattedRider = await formatSingleRider(rider);
            formatted.push(formattedRider);
        }
        return formatted;
    };

    /**
     * Inside of the modal, each rider displays the rider's name, barn name and location
     * Format the riders to have proper values for each of these data items.
     * @param rider
     * @returns formattedOption
     */
    const formatSingleRider = async (rider: Rider) => {
        const value = rider.id;
        const name = rider.name || "Unknown - No name found";
        const status = rider.isProfessional === true ? "Professional" : rider.isProfessional === false ? "Amateur" : "Unknown";

        // Have to query each time for the barn name due to the way Rider.graphql is structured.
        let barnName = "Unknown";
        if (rider.barnId) {
            const barnQuery = await getBarnById(rider.barnId);
            if (barnQuery.isSuccess) {
                const riderBarn: Barn = barnQuery.result;
                barnName = riderBarn.name;
            }
        }

        let object: formattedOption = {
            value: value,
            label: name + " " + status + " " + barnName,
            riderName: name,
            riderNameLabel: name,
            barnLabel: barnName,
            riderStatusLabel: status,
            rider: rider,
        };
        return object;
    };

    /**
     * Allow user to search the matched riders by rider name, barn, or location
     */
    const handleSearchText = (text: string) => {
        setSearchText(text);
        if (text) {
            // Filter by text
            const filtered = formattedMatchedRiders?.filter(
                (rider) =>
                    rider.riderName.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
                    (rider.barnLabel && rider.barnLabel.toLocaleLowerCase().includes(text.toLocaleLowerCase())) ||
                    (rider.riderStatusLabel && rider.riderStatusLabel.toLocaleLowerCase().trim().includes(text.toLocaleLowerCase().trim()))
            );
            setFilteredFormattedMatchedRiders(filtered);
        } else if (text === "" && formattedMatchedRiders) {
            // Result the filtered list to the list of all formattedMatchedRiders
            setFilteredFormattedMatchedRiders(formattedMatchedRiders);
        } else {
            setFilteredFormattedMatchedRiders([]);
        }
    };

    /**
     * User found the rider by name in the RSP Database
     * Send that rider info back to the parent component
     * And close the modal
     */
    const handleClickUseMatchedRider = (formattedRider: formattedOption) => {
        handleUseMatchedRider(formattedRider);
    };

    const handleClickCreateNewRider = () => {
        handleCreateNewRider(confirmedRiderName);
    };

    return (
        <BasicModal
            isOpen={true}
            onClose={handleCancel}
            title="Verify Rider"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: handleCancel,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="text-center">
                        <h4 className="text-info font-weight-bold">{currentValue?.riderName ? "Is this the rider's name?" : "What is the rider's name?"}</h4>
                        <IonItem color="white">
                            <IonInput
                                type="text"
                                value={unConfirmedRiderName}
                                aria-required={true}
                                onIonChange={(e) => {
                                    setUnConfirmedRiderName(e.detail.value!);
                                }}
                            />
                        </IonItem>
                        <IonButton className="mt-3" color="success" onClick={() => handleClickConfirm()}>
                            Confirm
                        </IonButton>
                    </IonCol>
                </IonRow>
                {isNameConfirmed && (
                    <>
                        <hr className="mb-0" />
                        {formattedMatchedRiders && formattedMatchedRiders.length > 0 ? (
                            // There are riders that match the confirmed rider name
                            // Use the filtered riders to display the options to the user and allow search
                            <>
                                {/* Keep the Search Bar and Match Info at the top level */}
                                <IonRow className="m-0">
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">
                                            {formattedMatchedRiders?.length || 0} Match{formattedMatchedRiders && formattedMatchedRiders?.length !== 1 && "es"} for{" "}
                                            {confirmedRiderName}
                                        </h4>
                                    </IonCol>
                                </IonRow>
                                {formattedMatchedRiders && formattedMatchedRiders?.length > 3 && (
                                    <IonRow>
                                        <IonCol className="m-0 p-0">
                                            <IonSearchbar
                                                className="m-0 pr-0"
                                                value={searchText}
                                                placeholder="Find by name, location or barn"
                                                onIonChange={(e) => handleSearchText(e.detail.value!)}
                                                color="white"></IonSearchbar>
                                        </IonCol>
                                    </IonRow>
                                )}
                                {filteredFormattedMatchedRiders && filteredFormattedMatchedRiders.length > 0 ? (
                                    <IonRow className="small-scrollable bg-light">
                                        <IonCol>
                                            {filteredFormattedMatchedRiders.map((formattedRider, index) => (
                                                <IonRow key={index}>
                                                    <IonCol className="bg-light p-0">
                                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                                            <IonRow className="ion-align-items-space-between py-0">
                                                                <IonCol sizeXs="12" sizeMd="8" className="p-0 ion-text-left">
                                                                    <IonCardTitle>
                                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{formattedRider.riderName}</h4>
                                                                    </IonCardTitle>
                                                                    <p className="text-white m-0">Barn: {formattedRider.barnLabel}</p>
                                                                    <p className="text-white m-0">Rider Status: {formattedRider.riderStatusLabel}</p>
                                                                </IonCol>
                                                                <IonCol sizeXs="12" sizeMd="4" className="ion-text-center align-content-center p-0">
                                                                    <IonRow className="ion-justify-content-center">
                                                                        <IonCol sizeXs="8" sizeMd="12" className="p-0">
                                                                            <IonButton
                                                                                className="py-1 m-0"
                                                                                expand="block"
                                                                                color="primary"
                                                                                onClick={() => handleClickUseMatchedRider(formattedRider)}>
                                                                                Select
                                                                            </IonButton>
                                                                        </IonCol>
                                                                    </IonRow>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonCard>
                                                    </IonCol>
                                                </IonRow>
                                            ))}
                                        </IonCol>
                                    </IonRow>
                                ) : (
                                    <IonRow>
                                        <IonCol>
                                            <p>There are no options that match this search.</p>
                                        </IonCol>
                                    </IonRow>
                                )}
                            </>
                        ) : (
                            <IonRow>
                                <IonCol className="text-center text-danger">
                                    <p>No matches for this rider name in the RingSide Pro database.</p>
                                </IonCol>
                            </IonRow>
                        )}
                        <hr className="m-0" />

                        <IonRow className="my-0">
                            <IonCol size="12">
                                <IonRow>
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">Can't find the rider?</h4>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="p-0">
                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                            <IonRow className="ion-align-items-space-between py-0">
                                                <IonCol sizeXs="12" sizeMd="7" className="p-0 ion-text-left">
                                                    <IonCardTitle>
                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{confirmedRiderName}</h4>
                                                    </IonCardTitle>
                                                    <p className="text-white ion-text-wrap m-0">Barn: {selectedBarn?.name || currentValue?.barnLabel || "Unknown"}</p>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="5" className="p-0 ion-text-center align-content-center">
                                                    <IonButton className="py-1 m-0" color="tertiary" onClick={() => handleClickCreateNewRider()}>
                                                        Create New Rider
                                                    </IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            </IonCol>
                        </IonRow>
                    </>
                )}
            </>
        </BasicModal>
    );
};
export default VerifyRiderModal;
